import { Box, CircularProgress } from '@mui/material';
import { FaceValidation } from 'idmission-web-sdk';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { UserRefresher } from '@/components/AppRouter/UserRefresher';
import { ErrorMessage } from '@/components/Idmission/ErrorMessage';
import {
  IdmClassNames,
  IdmStylesMobile,
} from '@/components/Idmission/Idmission.styled';
import { RejectedMessage } from '@/components/Idmission/RejectedMessage';

import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useIdmissionVerbiage } from '@/hooks/verification/useIdmissionVerbiage';
import { useIdmissionVerification } from '@/hooks/verification/useIdmissionVerification';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import {
  checkUserEkycStatus,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

export default function IdmissionFaceValidationMobile() {
  const user = useAppSelector(selectLoggedInUser);
  const { isSelfieChecked, isLivenessVideoRecorded } =
    checkUserEkycStatus(user);
  const navigate = useNavigate();
  const { verbiage } = useIdmissionVerbiage();

  const onCompleted = () => {
    if (isExistingCustomer(user) || isRemediationCustomer(user)) {
      navigate(ROUTE.MORE_INFORMATION);
    } else {
      navigate(ROUTE.BASIC_INFO);
    }
  };

  const {
    isProcessing,
    isCompleted,
    sessionId,
    error,
    idImageBase64,
    idImageLoading,
    isIDRejected,
    isSelfieRejected,
    isLivenessRejected,
    acknowledgeRejection,
    handleBeforeLivenessCheck,
    handleComplete,
    handleAcknowledgeRejection,
    resetVerification,
    handleOnRequestFailure,
  } = useIdmissionVerification({
    type: 'face',
    isSelfieChecked,
    isLivenessVideoRecorded,
    onCompleted,
  });

  const handleOnExit = useCallback(() => {
    resetVerification();
  }, [resetVerification]);

  if (
    (isIDRejected || isSelfieRejected || isLivenessRejected) &&
    !acknowledgeRejection
  ) {
    return (
      <RejectedMessage
        isIDRejected={isIDRejected}
        isSelfieRejected={isSelfieRejected}
        isLivenessRejected={isLivenessRejected}
        onAcknowledge={handleAcknowledgeRejection}
      />
    );
  }

  if (isProcessing || !sessionId || idImageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isCompleted) {
    return <UserRefresher delayMs={3000} />;
  }
  if (error) {
    return <ErrorMessage error={error} onAcknowledge={handleOnExit} />;
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw' }}>
      <IdmStylesMobile>
        <FaceValidation
          sessionId={sessionId}
          onBeforeSubmit={handleBeforeLivenessCheck}
          onComplete={handleComplete}
          onDenied={handleComplete}
          onExitCapture={handleOnExit}
          onExitAfterFailure={handleOnExit}
          onRequestFailure={handleOnRequestFailure}
          classNames={IdmClassNames.faceLiveness}
          verbiage={verbiage.faceLiveness}
          idCardForFaceMatch={idImageBase64 || undefined}
        />
      </IdmStylesMobile>
    </Box>
  );
}
